import { useDispatch, useSelector } from 'react-redux';
import { AnyAction, ThunkDispatch, unwrapResult } from '@reduxjs/toolkit';
import { useRouter } from 'next/router';

import { artworksSelectors, artworksActions } from '@modules/artworks';
import { tradeArtworksSelectors, tradeArtworksActions } from '@modules/tradeArtworks';
import { groupingArtworksSelectors, groupingArtworksActions } from '@modules/groupingArtworks';
import { ROUTE } from '@utils/constants';
import {
  Artwork,
  ChangeGridSizePayload,
  FetchArtworksState,
  FetchGroupingArtworksState,
  SetInitialArtworkPayload,
} from '@definitions/artworks.types';
import { GlobalState } from '@modules/createStore';
import { useBreakpoint } from '@hooks/useBreakpoint';

type AppDispatch = ThunkDispatch<GlobalState, any, AnyAction>;

export const useChangeGridSize = () => {
  const dispatch = useDispatch();

  const gridSize = useSelector(artworksSelectors.selectGridSize);
  const changeGridSize = (data: ChangeGridSizePayload) => dispatch(artworksActions.changeGridSize(data));

  return { gridSize, changeGridSize };
};

export const useFetchArtworks = () => {
  const dispatch: AppDispatch = useDispatch();

  const artworks = useSelector(artworksSelectors.selectArtworks);
  const meta = useSelector(artworksSelectors.selectMeta);
  const loading = useSelector(artworksSelectors.selectLoading);
  const fetchArtworks = (data: FetchArtworksState) => dispatch(artworksActions.fetchArtworks(data)).then(unwrapResult);

  return { artworks, meta, loading, fetchArtworks };
};

export const useFetchTradeArtworks = () => {
  const dispatch: AppDispatch = useDispatch();

  const artworks = useSelector(tradeArtworksSelectors.selectArtworks);
  const meta = useSelector(tradeArtworksSelectors.selectMeta);
  const loading = useSelector(tradeArtworksSelectors.selectLoading);
  const fetchTradeArtworks = (data: FetchArtworksState) =>
    dispatch(tradeArtworksActions.fetchTradeArtworks(data)).then(unwrapResult);

  return { artworks, meta, loading, fetchTradeArtworks };
};

export const useFetchFavoriteTradeArtworks = () => {
  const dispatch: AppDispatch = useDispatch();

  const favoriteTradeArtworks = useSelector(tradeArtworksSelectors.selectFavoriteArtworks);
  const fetchFavoriteTradeArtworks = () =>
    dispatch(tradeArtworksActions.fetchFavoriteTradeArtworks()).then(unwrapResult);

  return { favoriteTradeArtworks, fetchFavoriteTradeArtworks };
};

export const useFetchGroupingArtworks = () => {
  const dispatch: AppDispatch = useDispatch();

  const groupingArtworks = useSelector(groupingArtworksSelectors.selectArtworks);
  const groupingMeta = useSelector(groupingArtworksSelectors.selectMeta);
  const groupingLoading = useSelector(groupingArtworksSelectors.selectLoading);
  const fetchGroupingArtworks = (data: FetchGroupingArtworksState) =>
    dispatch(groupingArtworksActions.fetchArtworks(data)).then(unwrapResult);

  return { groupingArtworks, groupingMeta, groupingLoading, fetchGroupingArtworks };
};

export const useFetchFavoriteArtworks = () => {
  const dispatch: AppDispatch = useDispatch();

  const favoriteArtworks = useSelector(artworksSelectors.selectFavoriteArtworks);
  const fetchFavoriteArtworks = () => dispatch(artworksActions.fetchFavoriteArtworks()).then(unwrapResult);

  return { favoriteArtworks, fetchFavoriteArtworks };
};

export const useFetchFavoriteGroupingArtworks = () => {
  const dispatch: AppDispatch = useDispatch();

  const favoriteGroupingArtworks = useSelector(groupingArtworksSelectors.selectFavoriteArtworks);
  const fetchFavoriteGroupingArtworks = (data: FetchGroupingArtworksState) =>
    dispatch(groupingArtworksActions.fetchFavoriteArtworks(data)).then(unwrapResult);

  return { favoriteGroupingArtworks, fetchFavoriteGroupingArtworks };
};

export const useSetInitialGroupingArtworks = () => {
  const dispatch = useDispatch();

  const groupingArtworks = useSelector(groupingArtworksSelectors.selectArtworks);
  const setInitialGroupingArtworks = (data: SetInitialArtworkPayload) =>
    dispatch(groupingArtworksActions.setInitialArtworks(data));

  return { groupingArtworks, setInitialGroupingArtworks };
};

export const useSetInitialArtworks = () => {
  const dispatch = useDispatch();

  const artworks = useSelector(artworksSelectors.selectArtworks);
  const setInitialArtworks = (data: SetInitialArtworkPayload) => dispatch(artworksActions.setInitialArtworks(data));

  return { artworks, setInitialArtworks };
};

export const useSetInitialTradeArtworks = () => {
  const dispatch = useDispatch();

  const tradeArtworks = useSelector(tradeArtworksSelectors.selectArtworks);
  const setInitialTradeArtworks = (data: SetInitialArtworkPayload) => {
    dispatch(tradeArtworksActions.setInitialArtworks(data));
  };

  return { tradeArtworks, setInitialTradeArtworks };
};

export const useResetArtworks = () => {
  const dispatch = useDispatch();

  const resetArtworks = () => dispatch(artworksActions.resetArtworks());

  return { resetArtworks };
};

export const useResetTradeArtworks = () => {
  const dispatch = useDispatch();

  const resetArtworks = () => dispatch(tradeArtworksActions.resetArtworks());

  return { resetArtworks };
};

export const useSetCurrentPage = () => {
  const dispatch = useDispatch();

  const currentPage = useSelector(artworksSelectors.selectCurrentPage);
  const setCurrentPage = (page: number) => dispatch(artworksActions.setCurrentPage(page));

  return { setCurrentPage, currentPage };
};

export const useSetCurrentTradePage = () => {
  const dispatch = useDispatch();

  const currentTradePage = useSelector(tradeArtworksSelectors.selectCurrentPage);
  const setCurrentTradePage = (page: number) => dispatch(tradeArtworksActions.setCurrentPage(page));

  return { setCurrentTradePage, currentTradePage };
};

export const useResetGroupingArtworks = () => {
  const dispatch = useDispatch();

  const resetGroupingArtworks = () => dispatch(groupingArtworksActions.resetArtworks());

  return { resetGroupingArtworks };
};

export const useSetCurrentGroupingPage = () => {
  const dispatch = useDispatch();

  const currentGroupingPage = useSelector(groupingArtworksSelectors.selectCurrentPage);
  const setCurrentGroupingPage = (page: number) => dispatch(groupingArtworksActions.setCurrentPage(page));

  return { setCurrentGroupingPage, currentGroupingPage };
};

export const useSellingState = (
  artwork: Artwork
): {
  isBeforeSellingDate: boolean;
  isWithinSellingDates: boolean;
  isPrelaunchable: boolean;
  isEnding: boolean;
  renderExpiryDate: () => string;
} => {
  const { liveStartAt, liveEndAt, inventoryLevel } = artwork;
  const now: number = new Date().getTime();
  const oneMonth: number = 60 * 60 * 1000 * 24 * 30;
  const startSellingDate: number = new Date(liveStartAt).getTime();
  const endSellingDate: number = new Date(liveEndAt).getTime();
  const isBeforeSellingDate: boolean = now < startSellingDate;
  const isWithinSellingDates: boolean = now > startSellingDate && now < endSellingDate;
  const isPrelaunchable: boolean = inventoryLevel <= 0 && isBeforeSellingDate;
  const isEnding: boolean = endSellingDate - now < oneMonth;

  function renderExpiryDate(): string {
    const date = new Date(endSellingDate);
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  }

  return { isBeforeSellingDate, isWithinSellingDates, isPrelaunchable, isEnding, renderExpiryDate };
};

export const useWaitlistButtonLabel = ({
  isShoppable,
  isExclusive = false,
  isPdp = false,
}: {
  isShoppable: boolean;
  isExclusive?: boolean;
  isPdp?: boolean;
}): string => {
  const { isMobile } = useBreakpoint();
  const waitlistLabel = isMobile || isPdp ? 'WAITLIST' : 'ADD TO WAITLIST';
  const notifyLabel = isMobile && isPdp ? 'NOTIFY' : 'NOTIFY ME';

  if (isExclusive) {
    if (isShoppable) {
      return waitlistLabel;
    }

    return notifyLabel;
  }

  return waitlistLabel;
};

export const useArtworksRoute = () => {
  const router = useRouter();
  const isGroupingRoute = router.pathname?.includes(ROUTE.GROUPINGS);
  const isTradeMemberRoute = router.pathname === ROUTE.TRADE;
  const isArtworksRoute = router.pathname === ROUTE.ARTWORKS;

  return { isGroupingRoute, isArtworksRoute, isTradeMemberRoute };
};
